import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/commerce-php/commerce-php/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
import Docs from '/src/_includes/braintree-note.md';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <Docs mdxType="Docs" />
    <h1 {...{
      "id": "adding-vault-integration"
    }}>{`Adding vault integration`}</h1>
    <p>{`Vault as a payment method provides store customers with ability to use the previously saved credit card information for checkout. This information is stored safely on the side of trusted payments gateways (Braintree, PayPal). Not storing the sensitive credit card information is one of the `}<a parentName="p" {...{
        "href": "https://www.pcisecuritystandards.org/"
      }}>{`PCI compliance`}</a>{` requirements.`}</p>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://github.com/magento/magento2/tree/2.4/app/code/Magento/Vault"
      }}>{`Commerce Vault`}</a>{` is implemented using the Adobe Commerce `}<a parentName="p" {...{
        "href": "../payment-gateway/index.md"
      }}>{`payment gateway`}</a>{`. It is used in the out-of-the-box vault implementation for the Braintree payment method. Third party developers can use it to add vault to their custom payment provider integration.`}</p>
    <p>{`The following diagram shows a simplified interaction flow between Commerce sales management, vault, payment integration and external payment service provider.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "700px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "45.9375%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/php/static/e9839676092b92055487add900d73317/5530d/vault_interaction_flow1.webp 320w", "/commerce/php/static/e9839676092b92055487add900d73317/0c8fb/vault_interaction_flow1.webp 640w", "/commerce/php/static/e9839676092b92055487add900d73317/84c6d/vault_interaction_flow1.webp 700w"],
            "sizes": "(max-width: 700px) 100vw, 700px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/php/static/e9839676092b92055487add900d73317/dd4a7/vault_interaction_flow1.png 320w", "/commerce/php/static/e9839676092b92055487add900d73317/0f09e/vault_interaction_flow1.png 640w", "/commerce/php/static/e9839676092b92055487add900d73317/a27c6/vault_interaction_flow1.png 700w"],
            "sizes": "(max-width: 700px) 100vw, 700px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/commerce/php/static/e9839676092b92055487add900d73317/a27c6/vault_interaction_flow1.png",
            "alt": "Vault Interaction",
            "title": "Vault Interaction",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`The flow is similar to usual Commerce `}<a parentName="p" {...{
        "href": "../payment-gateway/index.md"
      }}>{`payment gateway interaction flow`}</a>{`.`}</p>
    <p>{`The main difference is that when vault is used, it defines what command is required and when it must be called.`}</p>
    <h2 {...{
      "id": "adding-vault-general-steps"
    }}>{`Adding vault: general steps`}</h2>
    <p>{`Commerce vault implementation provides that vault is available as a separate payment method during order creation (storefront or admin). But technically it is tightly related to the corresponding payment provider integration.
You do not need to create a new module for vault implementation. All required configuration and entities are added to the module of the payment provider integration.`}</p>
    <p>{`The topics of this chapter describe how to add the vault functionality to a payment method (the payment method must be implemented using Commerce payment gateway):`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li"><a parentName="p" {...{
            "href": "module-configuration.md"
          }}>{`Add vault to module dependencies`}</a>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><a parentName="p" {...{
            "href": "vault-payment-configuration.md"
          }}>{`Configure vault general parameters`}</a>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><a parentName="p" {...{
            "href": "vault-di.md"
          }}>{`Add vault and payment methods entities using dependency injection configuration`}</a>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><a parentName="p" {...{
            "href": "enabler.md"
          }}>{`Implement the ability for customers to choose whether to use vault`}</a>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><a parentName="p" {...{
            "href": "payment-token.md"
          }}>{`Storing and processing the payment related data`}</a>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><a parentName="p" {...{
            "href": "token-ui-component-provider.md"
          }}>{`Adding and using UI_Vault component to place orders on the storefront`}</a>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><a parentName="p" {...{
            "href": "customer-stored-payments.md"
          }}>{`Display stored tokens for customer and process their deleting`}</a>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><a parentName="p" {...{
            "href": "admin-integration.md"
          }}>{`Using stored tokens to place an order from Admin panel`}</a>{`.`}</p>
      </li>
    </ol>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      